import { ApplicationConfig, DEFAULT_CURRENCY_CODE, inject, Injector, LOCALE_ID, PLATFORM_ID, importProvidersFrom } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { routes } from './app.routes';
import { provideApollo as provideApollo_alias } from 'apollo-angular';
import { ApolloLink, DefaultOptions, FetchResult, InMemoryCache, Observable } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { onError } from '@apollo/client/link/error';
import { authInterceptor } from "./core/auth/auth.interceptor";
import { errorInterceptor } from './core/auth/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import Swal from "sweetalert2";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { CURRENCY_MASK_CONFIG } from "ng2-currency-mask";
import { provideNgxMask } from "ngx-mask";
import { NativeDateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter, provideNativeDateAdapter } from "@angular/material/core";
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { environment } from "../environments/environment";
import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';

// Garante que qualquer data digitada como DD/MM/AAAA seja lida perfeitamente pelo calendário
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  constructor() {
    super(inject(MAT_DATE_LOCALE, { optional: true }) as string || 'pt-BR', inject(Platform));
  }

  override parse(value: any): Date | null {
    if ((typeof value === 'string') && (value.indexOf('/') > -1)) {
      const str = value.split('/');
      const date = Number(str[0]);
      const month = Number(str[1]) - 1;
      const year = Number(str[2]);
      if (year >= 1000 && year <= 9999 && date >= 1 && date <= 31 && month >= 0 && month <= 11) {
        return new Date(year, month, date);
      }
    }
    return super.parse(value);
  }
}

export function customDateAdapterFactory() {
  return new CustomDateAdapter();
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(MatDialogModule),
        provideRouter(routes),
        // errorInterceptor por fora: o authInterceptor resolve o 401 (refresh +
        // retry) antes de o erro chegar ao alerta genérico.
        provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
        provideNgxMask(),
        provideNativeDateAdapter(),
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        HttpLink,
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
        {
            provide: CURRENCY_MASK_CONFIG,
            useValue: {
                align: "right",
                allowNegative: true,
                decimal: ",",
                precision: 2,
                prefix: "R$ ",
                suffix: "",
                thousands: "."
            }
        },
        provideApollo_alias(() => {
            const platformId = inject(PLATFORM_ID);
            const httpLink = inject(HttpLink);
            // AuthService não pode ser injetado aqui (ciclo: AuthService → GQL gerado → Apollo);
            // resolvemos lazy via Injector dentro do handler do errorLink.
            const injector = inject(Injector);

            if (!isPlatformBrowser(platformId)) {
                return {
                    cache: new InMemoryCache(),
                };
            }

            const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
                const is401 = graphQLErrors?.some((e: any) =>
                    e?.extensions?.originalError?.statusCode === 401 ||
                    e?.extensions?.code === 'UNAUTHENTICATED'
                );
                const skipRefresh = ['refreshToken', 'loginCustomer', 'logout'].includes(operation.operationName);
                const alreadyRetried = operation.getContext()['retriedAfterRefresh'];

                if (is401 && !skipRefresh && !alreadyRetried) {
                    const authService = injector.get(AuthService);
                    return new Observable<FetchResult>(observer => {
                        authService.refreshSession().then(token => {
                            if (!token) {
                                authService.logout();
                                observer.complete();
                                return;
                            }
                            operation.setContext({ retriedAfterRefresh: true });
                            forward(operation).subscribe({
                                next: value => observer.next(value),
                                error: error => observer.error(error),
                                complete: () => observer.complete(),
                            });
                        });
                    });
                }

                // MODO SILENCIOSO TOTAL: Para evitar o flood de erros 400 no seu console
                if (graphQLErrors) {
                    graphQLErrors.forEach(({ message }) => {
                        console.debug('[GraphQL Error Handled Silently]', message);
                    });
                }
                if (networkError) {
                    console.debug('[Network Error Handled Silently]', networkError.message);
                }
                return;
            });

            const defaultOptions: DefaultOptions = {
                watchQuery: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none', // Modificado: Agora os erros sobem para a tela!
                },
                query: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none',
                },
                mutate: {
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'none',
                }
            };

            return {
                link: ApolloLink.from([
                    errorLink,
                    httpLink.create({ uri: environment.base_graphql, withCredentials: true }),
                ]),
                cache: new InMemoryCache(),
                defaultOptions,
            };
        }),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    // darkModeSelector: '.app-dark',
                    cssLayer: {
                        name: 'primeng',
                        order: 'theme, base, primeng'
                    }
                }
            }
        }),

    ],
};

registerLocaleData(localePt);
